import React, { useState } from 'react';
import { AlertModal } from '@/components/modal/alert-modal';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ProfilePanel } from '@/modules/profile/profile-sheet';
import { OrganisationInviteModal } from '../organisation-invite-modal';
import { CircleCheck, MoreHorizontal, Trash2, UserPlus } from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import type { Organisation } from '@/services/types';
import { toast } from 'sonner';
import { useDeleteOrganisationsMutation } from '@/services/organisations/queries';

interface CellActionProps {
  data: Organisation;
}

export const CellAction: React.FC<CellActionProps> = ({ data }) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const { user } = useAuthStore();
  
  const isAdmin = user?.workspace || false;
  const inviteEmail = data.email || data.contactPerson?.email || '';

  const { mutate: deleteOrganisations, isPending } = useDeleteOrganisationsMutation(
    () => {
      toast.success('Organisations deleted successfully');
      setOpen(false);
    },
    (error) => {
      toast.error(error.response.data.message || 'Failed to delete organisations');
      setOpen(false);
    }
  );
      
  const onConfirm = async () => {
    deleteOrganisations({ organisationIds: [data.id] });
    setOpen(false);
  };

  return (
    <div onClick={(e) => e.stopPropagation()} className='relative w-full'>
      <AlertModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onConfirm={onConfirm}
        loading={isPending}
        title='Delete organisation'
        description={`Are you sure you want to delete ${data.name}?`}
      />

      <OrganisationInviteModal
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        defaultEmail={inviteEmail}
        defaultOrganisationId={data.id}
        defaultOrganisationName={data.name}
        lockOrganisation
      />

      {!isAdmin && (
        <Button
          variant={'ghost'}
          onClick={() => setIsPanelOpen(true)}
          id='organisations-view-profile'
        >
          View Profile
        </Button>
      )}
      {isAdmin && (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='h-8 w-8 p-0'>
              <span className='sr-only'>Open menu</span>
              <MoreHorizontal className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuLabel>Actions</DropdownMenuLabel>

            <DropdownMenuItem onClick={() => setIsPanelOpen(true)}>
              <CircleCheck className='mr-2 h-4 w-4' /> View Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setInviteOpen(true)}>
              <UserPlus className='mr-2 h-4 w-4' /> Invite
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setOpen(true)}
              className='text-[#C5292A]'
            >
              <Trash2 className='mr-2 h-4 w-4' /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      { isPanelOpen && (
        <ProfilePanel
          key={data.id} // Key to prevent unmounting issues
          data={data}
          open={isPanelOpen}
          onOpenChange={setIsPanelOpen}
        />
      )}
    </div>
  );
};
